import { Component, computed, effect, inject, OnDestroy, SecurityContext, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonCard,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonModal,
  IonText,
  IonTitle,
  IonToolbar,
  ModalController,
} from '@ionic/angular/standalone';
import { VxCurrencyPipe } from '@libs/core/lib/pipes/mtjr-currency.pipe';
import { OrderChatPage } from '@libs/mtjr/shared/orders/order-chat.page';

import { DatePipe, NgStyle } from '@angular/common';
import { ApiService } from '@libs/core/base-config';
import { VxErrorComponent } from '@libs/core/lib/error-view/error-view.component';
import { VxLoadingComponent } from '@libs/core/lib/shared/loading-state';
import { VxImageComponent } from '@libs/core/lib/shared/mtjr-image.component';
import { asResult, dataFromState } from '@libs/core/lib/state/async-states';
import { AppAlerts } from '@libs/core/lib/utils/app-alerts';
import { withCancelOrder, withGetOrderById } from '@libs/mtjr/shared/generated/entities/order/order-store-features';
import { getOrderById } from '@libs/mtjr/shared/generated/entities/order/service/order.service';
import { patchState, signalStore, withComputed, withMethods } from '@ngrx/signals';

import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgEventBus } from '@libs/core/lib/event-bus/ng-event-bus';

import { LangSelectPipe } from '@libs/core/lib/pipes/lang-select.pipe';
import { Entity } from '@libs/mtjr/shared/generated/types';
import { OrderStatusTextComponent } from '@libs/mtjr/shared/orders/order-status-text.component';
import { VxQrCodeComponent } from '@libs/mtjr/shared/shared/mtjr-qr-code.component';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

export const OrderDetailsStore = signalStore(
  withGetOrderById(),
  withCancelOrder(),

  withMethods((store, apiService = inject(ApiService)) => ({
    async refreshOrder(orderId: string) {
      const result = await asResult(getOrderById(apiService, { id: orderId }));
      patchState(store, { getOrderByIdState: result });
    },
  })),

  withComputed((store) => ({
    order: dataFromState(store.getOrderByIdState, (data) => data),
  })),

  withComputed((store) => ({
    onlyDigitalProducts: computed(() => {
      const order = store.order();
      if (!order) return false;
      return order.subOrders.every((subOrder) =>
        subOrder.orderProducts.every((orderProduct) => orderProduct.productType === 'digital'),
      );
    }),
  })),
);
@Component({
  selector: 'mac-order-details',
  providers: [OrderDetailsStore],
  standalone: true,
  imports: [
    IonLabel,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonBackButton,
    IonContent,
    IonCard,
    IonText,
    IonButton,
    IonIcon,
    VxCurrencyPipe,
    VxLoadingComponent,
    VxErrorComponent,
    DatePipe,
    VxImageComponent,
    IonItem,
    IonModal,
    OrderChatPage,
    OrderStatusTextComponent,
    TranslatePipe,
    VxQrCodeComponent,
    NgStyle,
    LangSelectPipe,
    RouterLink,
  ],
  templateUrl: 'order-details.page.html',
})
export class OrderDetailsPage implements OnDestroy {
  store = inject(OrderDetailsStore);
  route = inject(ActivatedRoute);
  appAlerts = inject(AppAlerts);
  tab = signal('details');
  orderId: string = this.route.snapshot.params['orderId'];
  private sanitizer = inject(DomSanitizer);
  alerts = inject(AppAlerts);
  router = inject(Router);
  routeSub?: Subscription;
  eventBusSub?: Subscription;
  eventBusChatSub?: Subscription;
  modalCtrl = inject(ModalController);
  eventBus = inject(NgEventBus);

  openedChat = signal<Entity.SubOrder | undefined>(undefined);
  productPlaceholder = 'https://nbg1.your-objectstorage.com/mtjr-prod/product-placeholder.webp';

  constructor() {
    this.store.getOrderById({ id: this.orderId });

    this.routeSub = this.route.queryParams.subscribe((params) => {
      const subOrderId = params['order-chat'];
      this.onSubOrderQuery(subOrderId);
    });

    this.eventBusSub = this.eventBus.on<{ orderId: string }>('order:fetch').subscribe((event) => {
      const orderId = event.data?.orderId;
      if (orderId && orderId === this.orderId) {
        this.store.refreshOrder(orderId);
      }
    });

    effect(() => {
      const openedChat = this.openedChat();
      if (openedChat) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { 'order-chat': openedChat.id },
          queryParamsHandling: 'merge',
        });
      } else {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { 'order-chat': null },
          queryParamsHandling: 'merge',
        });
      }
    });
  }

  private onSubOrderQuery(subOrderId: string | null) {
    if (subOrderId) {
      const subOrder = this.store.order()?.subOrders.find((so) => so.id === subOrderId);
      if (subOrder) {
        this.openedChat.set(subOrder);
      }
    } else {
      this.openedChat.set(undefined);
    }
  }

  onSegmentChanged(event: CustomEvent) {
    this.tab.set(event.detail.value);
  }

  wPayPayment(safeUrl: SafeResourceUrl) {
    const url = this.sanitizer.sanitize(SecurityContext.RESOURCE_URL, safeUrl);
    if (url) {
      window.open(url, '_blank');
    }
  }

  copyToClipboard(value: string) {
    navigator.clipboard.writeText(value);
    this.alerts.successSnackbarTR('COMMON.COPIED_TO_CLIPBOARD', { position: 'top' });
  }

  productImage(item: Entity.OrderProduct) {
    return item.productImage ?? item.variationImage ?? this.productPlaceholder;
  }

  variationAttributes(item: Entity.OrderProduct): Entity.VariationAttribute[] {
    return Array.isArray(item.variationAttributes) ? item.variationAttributes : [];
  }

  ngOnDestroy() {
    this.routeSub?.unsubscribe();
    this.openedChat.set(undefined);
    this.modalCtrl.dismiss();
  }
}
